import { Lender } from '@1delta/lender-registry'
import { Call } from '../../../utils/multicall'
import {
  getAaveAssets,
  getAaveStyleProtocolTokenMap,
  getAaveTypeEModeCount,
  getAaveTypePoolAddress,
  getAaveTypePoolDataProviderAddress,
} from '../../../assets'
import { isAaveV32Type } from '@1delta/lender-registry'
import { Chain } from '@1delta/chain-registry'

const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000'

// ERC20 `balanceOf` fragment — attached per-call to override the default lender
// ABI. Used to read the aToken's physical underlying balance (available
// liquidity) since Aave V3's data provider no longer returns it.
const ERC20_BALANCE_OF_ABI = [
  {
    inputs: [{ name: 'account', type: 'address' }],
    name: 'balanceOf',
    outputs: [{ name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
] as const

function balanceOfCall(underlying: string, aToken: string | undefined): Call {
  return {
    // @ts-ignore — per-call abi override picked up by fetchLender.ts
    abi: ERC20_BALANCE_OF_ABI,
    address: underlying,
    name: 'balanceOf',
    params: [aToken ?? ZERO_ADDRESS],
  }
}

function range(n: number): number[] {
  return Array.from({ length: n + 1 }, (_, i) => i)
}

/**
 * E-mode category ids to query for an Aave-V3-style deployment.
 *
 * The pool exposes NO count getter (only `getEModeCategoryData(uint8)` &c), so
 * the number of categories cannot be derived on-chain in the same multicall
 * round. It comes from `config/aave-pools.json`'s `eModeCount`, probed daily by
 * `lender-metadata`'s `update:dataset`, so a new category is picked up by a
 * metadata publish rather than an npm release.
 *
 * Ids are contiguous from 1 on every deployment observed, hence a plain count.
 * Id 0 ("no e-mode") is always included — the parsers special-case it.
 *
 * The hardcoded table below is the FALLBACK for metadata that predates the
 * field or a deployment the probe could not reach. It is deliberately kept
 * generous: reading past the last category returns an empty struct that the
 * parsers filter out, so over-fetching only costs calls, while under-fetching
 * drops a live e-mode with no error anywhere.
 */
export const AAVE_V3_EMODES = (chain: string, lender: string) => {
  const eModeCount = getAaveTypeEModeCount(chain, lender)
  if (eModeCount !== undefined) return range(eModeCount)

  if (chain === Chain.ETHEREUM_MAINNET) {
    if (lender === Lender.AAVE_V3) return range(50)
    if (lender === Lender.AAVE_V3_PRIME) return range(12)
    if (lender === Lender.AAVE_V3_HORIZON) return range(12)
  }
  if (chain === Chain.ARBITRUM_ONE) {
    if (lender === Lender.AAVE_V3) return range(12)
  }
  if (chain === Chain.BASE) {
    if (lender === Lender.AAVE_V3) return range(18)
  }

  if (chain === Chain.PLASMA_MAINNET) {
    if (lender === Lender.AAVE_V3) return range(30)
  }

  return [0, 1, 2, 3, 4, 5, 6, 7, 8, 9]
}

function yldrBaseline(
  assetsToQuery: string[],
  providerAddress: string,
  aaveTokenMap: any,
) {
  return assetsToQuery.flatMap((tk) => [
    {
      address: providerAddress,
      name: 'getReserveData',
      params: [tk],
    },
    {
      address: providerAddress,
      name: 'getReserveConfigurationData',
      params: [tk],
    },
    balanceOfCall(tk, aaveTokenMap?.[tk]?.aToken),
  ])
}

function aaveV3baseline(
  assetsToQuery: string[],
  providerAddress: string,
  poolAddress: string,
  aaveTokenMap: any,
  legacy = false,
) {
  const callDecimals: Call = {
    address: providerAddress,
    name: 'getDebtCeilingDecimals',
    params: [],
  }

  const callReservesList: Call = {
    address: poolAddress,
    name: 'getReservesList',
    params: [],
  }

  const callsBase = assetsToQuery.flatMap((tk) => [
    {
      address: providerAddress,
      name: 'getReserveData',
      params: [tk],
    },
    {
      address: providerAddress,
      name: 'getReserveConfigurationData',
      params: [tk],
    },
    {
      address: providerAddress,
      name: 'getSiloedBorrowing',
      params: [tk],
    },
    {
      address: providerAddress,
      name: 'getReserveCaps',
      params: [tk],
    },
    {
      address: providerAddress,
      name: 'getDebtCeiling',
      params: [tk],
    },
    ...(legacy
      ? [
          {
            address: providerAddress,
            name: 'getReserveEModeCategory',
            params: [tk],
          },
        ]
      : []),
    balanceOfCall(tk, aaveTokenMap?.[tk]?.aToken),
  ])

  return {
    callsBase,
    callReservesList,
    callDecimals,
  }
}

export const buildAaveV3StyleLenderReserveCall = (
  chainId: string,
  lender: string,
) => {
  const providerAddress = getAaveTypePoolDataProviderAddress(chainId, lender)
  const assetsToQuery = getAaveAssets(chainId, lender)
  const poolAddress = getAaveTypePoolAddress(chainId, lender)
  const aaveTokenMap = getAaveStyleProtocolTokenMap(chainId, lender)
  if (isAaveV32Type(lender)) {
    const getEModeCategoryData: Call[] = AAVE_V3_EMODES(
      chainId,
      lender,
    ).flatMap((eMode) => {
      return [
        {
          address: poolAddress,
          name: 'getEModeCategoryData',
          params: [eMode],
        },
        {
          address: poolAddress,
          name: 'getEModeCategoryBorrowableBitmap',
          params: [eMode],
        },
        {
          address: poolAddress,
          name: 'getEModeCategoryCollateralBitmap',
          params: [eMode],
        },
      ]
    })

    const { callDecimals, callReservesList, callsBase } = aaveV3baseline(
      assetsToQuery,
      providerAddress,
      poolAddress,
      aaveTokenMap,
    )

    const calls = [
      ...callsBase,
      ...getEModeCategoryData,
      callReservesList,
      callDecimals,
    ]
    return calls
  }
  switch (lender) {
    case Lender.YLDR: {
      const calls = yldrBaseline(assetsToQuery, providerAddress, aaveTokenMap)
      return calls
    }
    default: {
      const getEModeCategoryData: Call[] = AAVE_V3_EMODES(
        chainId,
        lender,
      ).flatMap((eMode) => ({
        address: poolAddress,
        name: 'getEModeCategoryData',
        params: [eMode],
      }))

      const { callDecimals, callsBase } = aaveV3baseline(
        assetsToQuery,
        providerAddress,
        poolAddress,
        aaveTokenMap,
        true,
      )

      const calls = [...callsBase, ...getEModeCategoryData, callDecimals]
      return calls
    }
  }
}
